import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Input,
  InputWithOptions,
  RadioGroup,
  Text,
} from '@wix/design-system';
import s from '../modals.scss';
import { UserChoice } from '../../../../stores/premiumUsers2FaEnforcement/sms/addPhone';
import { AppContextProvider } from '../../../AppLoader';
import { useIsRtl } from '../../../../hooks';

interface AddPhoneStepContentProps {
  compact?: boolean;
}

/**
 * Shared content for the "add phone" step — the prompt + either the existing/new-number
 * radios or the country-code + phone-number fields. Rendered by both the old
 * `AddPhoneStep` (default layout) and the redesigned `AddPhoneRedesign` (`compact`).
 */
export const AddPhoneStepContent: React.FC<AddPhoneStepContentProps> = observer(
  ({ compact }) => {
    const {
      rootStore: {
        postLoginStore: {
          premiumUsers2FaEnforcementStore: { add2FAPhoneStore: store },
        },
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const prompt = (
      <Text>
        {t(
          store.isPrimaryExist
            ? 'add_phone_wizard.new_style.choose_phone'
            : 'add_phone_wizard.new_style.new_phone',
        )}
      </Text>
    );
    const body = store.isPrimaryExist ? (
      <ExistingOrAddingNumber compact={compact} />
    ) : (
      <AddingOrEditingFields compact={compact} />
    );
    if (compact) {
      return (
        <Box direction="vertical" width="100%" gap={3}>
          {prompt}
          {body}
        </Box>
      );
    }
    return (
      <>
        <Box marginBottom={4}>{prompt}</Box>
        {body}
      </>
    );
  },
);

const ExistingOrAddingNumber: React.FC<AddPhoneStepContentProps> = observer(
  ({ compact }) => {
    const {
      rootStore: {
        postLoginStore: {
          premiumUsers2FaEnforcementStore: { add2FAPhoneStore: store },
        },
      },
    } = useContext(AppContextProvider);
    const { internationalNumber } = store.getPrimary() || {};
    const { t } = useTranslation();
    const radios = (
      <RadioGroup
        value={store.userChoice}
        onChange={(value: string | number | undefined) => {
          if (typeof value === 'string' || typeof value === 'number') {
            store.userChoice = value as UserChoice;
          }
        }}
      >
        <RadioGroup.Radio value={UserChoice.PrimaryNumber}>
          <Text weight="normal" dir="ltr">
            {internationalNumber}
          </Text>
          <Text>{t('add_phone_wizard.add_phone.2fa.radio.existing')}</Text>
        </RadioGroup.Radio>
        <RadioGroup.Radio value={UserChoice.NewNumber}>
          {store.userChoice === UserChoice.NewNumber
            ? t('add_phone_wizard.add_phone.2fa.radio.new.selected')
            : t('add_phone_wizard.add_phone.2fa.radio.new')}
        </RadioGroup.Radio>
      </RadioGroup>
    );
    const showFields = store.userChoice === 1;

    if (compact) {
      return (
        <Box direction="vertical" width="100%" gap={2}>
          {radios}
          {showFields && <AddingOrEditingFields compact />}
        </Box>
      );
    }

    return (
      <>
        {radios}
        {showFields && (
          <Box marginTop={2}>
            <AddingOrEditingFields />
          </Box>
        )}
      </>
    );
  },
);

const AddingOrEditingFields: React.FC<AddPhoneStepContentProps> = observer(
  ({ compact }) => {
    const {
      rootStore: {
        postLoginStore: {
          premiumUsers2FaEnforcementStore: { add2FAPhoneStore: store },
        },
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const isRtl = useIsRtl();
    return (
      <Box
        gap={2}
        width={compact ? '100%' : undefined}
        flexFlow={isRtl ? 'row-reverse' : undefined}
      >
        <div dir="ltr">
          <InputWithOptions
            size={compact ? 'small' : undefined}
            selectedId={store.countryCode?.id}
            value={store.countryCode?.id}
            options={store.countryCodesOptions}
            onSelect={store.onSelectCountryCode}
            onChange={(e) => store.onSearchCountries(e.target.value)}
            prefix={store.countryCode?.icon}
            className={
              compact ? s.countriesDropdownCompact : s.countriesDropdown
            }
            popoverProps={{ appendTo: 'window', zIndex: '9999' } as any}
          />
        </div>
        {/* Redesign: phone input flex-grows to fill the card column; old: fixed width. */}
        <div dir="ltr" style={compact ? { flex: 1 } : undefined}>
          <Input
            size={compact ? 'small' : undefined}
            className={compact ? undefined : s.addPhoneStepInput}
            autocomplete="new-password"
            autoFocus
            placeholder={t('add_phone_wizard.add_phone.placeholder')}
            value={store.phoneNumber.value}
            onChange={store.phoneNumber.onChange}
            onBlur={store.phoneNumber.onBlur}
            status={
              !store.phoneNumber.isValid && store.phoneNumber.isDirty
                ? Input.StatusError
                : undefined
            }
            onEnterPressed={() => store.onSendVerificationSms()}
            statusMessage={store.phoneNumber.errorInfo}
          />
        </div>
      </Box>
    );
  },
);
